/**
 * Web implementation of the phone-verification seam (ELARA-730).
 *
 * On the browser Firebase requires an app verifier — an invisible reCAPTCHA mounted in a DOM element.
 * Owning that element here (rather than having the view render a container it must pass in) keeps the
 * shared `PhoneLinkState` machine DOM-free and platform-neutral. A fresh host + verifier is built per
 * attempt, matching reCAPTCHA's single-use contract: a resend goes through `startVerification` again.
 */
import type { AuthContainer } from '@elara/client-core/containers/FirebaseAuthContainer';
import type { PhoneVerificationPort } from '@elara/client-core/lib/phone-verification-port';

export function createWebPhoneVerificationPort(auth: AuthContainer): PhoneVerificationPort {
  return {
    async startVerification(phoneNumberE164) {
      // A hidden, document-attached host for the invisible reCAPTCHA widget Firebase mounts on demand.
      const host = document.createElement('div');
      host.style.display = 'none';
      document.body.append(host);
      const verifier = auth.createRecaptchaVerifier(host);

      const releaseVerifier = () => {
        try {
          verifier.clear();
        } catch (error) {
          // Expected when the widget was already torn down; warn (not throw) on anything else so a real
          // failure stays visible rather than silently leaking the reCAPTCHA widget.
          console.warn('[phone-verification.web] verifier.clear() failed (likely already cleared):', error);
        }
        host.remove();
      };

      try {
        const confirmationResult = await auth.linkPhoneNumber(phoneNumberE164, verifier);
        // The invisible-reCAPTCHA widget is spent once the OTP is sent — `ConfirmationResult.confirm`
        // uses the embedded verification id, not the verifier — so release it now. This leaves nothing
        // to clean up if the user abandons the code step, and a resend builds a clean widget.
        releaseVerifier();
        return {
          async confirm(code: string) {
            await confirmationResult.confirm(code);
          },
        };
      } catch (error) {
        releaseVerifier();
        throw error;
      }
    },
  };
}
